import { ArrowLeft, Calendar, TrendingUp, Zap, Sun, Droplet, Search, Share2, Clock, User } from 'lucide-react';
import { Fragment, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import remarkGfm from 'remark-gfm';
import { AD_SLOTS } from '../config/adConfig';
import { GoogleAd } from './ads/GoogleAd';
import { articles } from '../data/articles';

import { NotFoundPage } from './NotFoundPage';
import { BrandAnswerBox } from './BrandAnswerBox';
import { SolarLeadCTA } from './SolarLeadCTA';

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);

  // Scroll to top when article ID changes (i.e. viewing a new article)
  useEffect(() => {
    if (slug) {
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const categories = ['All', 'Power Generation', 'Renewable Energy', 'Oil & Gas'];

  const selectedArticle = slug ? articles.find(a => a.slug === slug || a.id === slug) : null;
  const selectedArticlePath = selectedArticle ? `/articles/${selectedArticle.slug}` : '';
  const selectedArticleUrl = selectedArticle ? `https://power.ng${selectedArticlePath}` : '';

  // 404 Handling: If ID is present but article not found, show 404
  if (slug && !selectedArticle) {
    return <NotFoundPage />;
  }

  if (slug && selectedArticle && slug !== selectedArticle.slug) {
    return <Navigate to={selectedArticlePath} replace />;
  }

  // Sort articles by date descending
  const sortedArticles = [...articles].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const filteredArticles = sortedArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Power Generation': return <Zap className="w-4 h-4" />;
      case 'Renewable Energy': return <Sun className="w-4 h-4" />;
      case 'Oil & Gas': return <Droplet className="w-4 h-4" />;
      default: return null;
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setShowShareModal(false);
      setTimeout(() => setShowShareToast(false), 2000);
    } catch (err) {
      // Fallback method
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      setShowShareToast(true);
      setShowShareModal(false);
      setTimeout(() => setShowShareToast(false), 2000);
    }
  };

  const shareToWhatsApp = () => {
    if (!selectedArticle) return;
    const text = `${selectedArticle.title}\n\n${selectedArticle.excerpt}\n\nRead more: ${window.location.href}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowShareModal(false);
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    setShowShareModal(false);
  };

  const shareToTwitter = () => {
    if (!selectedArticle) return;
    const text = `${selectedArticle.title}\n\n${selectedArticle.excerpt}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    setShowShareModal(false);
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
    setShowShareModal(false);
  };

  const shareToTelegram = () => {
    if (!selectedArticle) return;
    const text = selectedArticle.title;
    const url = `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowShareModal(false);
  };

  const useNativeShare = async () => {
    if (!selectedArticle) return;

    const shareData = {
      title: selectedArticle.title,
      text: selectedArticle.excerpt,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShowShareModal(false);
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Native share failed:', err);
      }
    }
  };

  // Detailed Article View
  if (selectedArticle) {
    const faqSchema = selectedArticle.faqs?.length ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": selectedArticle.faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    } : null;

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <Helmet>
          <title>{selectedArticle.title} - Power.ng</title>
          <meta name="description" content={selectedArticle.metaDescription || selectedArticle.excerpt} />
          <link rel="canonical" href={selectedArticleUrl} />

          {/* Open Graph */}
          <meta property="og:title" content={selectedArticle.title} />
          <meta property="og:description" content={selectedArticle.metaDescription || selectedArticle.excerpt} />
          <meta property="og:url" content={selectedArticleUrl} />
          <meta property="og:type" content="article" />
          <meta property="article:published_time" content={selectedArticle.date} />
          {selectedArticle.category && <meta property="article:section" content={selectedArticle.category} />}

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={selectedArticle.title} />
          <meta name="twitter:description" content={selectedArticle.metaDescription || selectedArticle.excerpt} />
          
          {/* JSON-LD Structured Data for Article / GEO */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": selectedArticle.title,
              "description": selectedArticle.metaDescription || selectedArticle.excerpt,
              "abstract": selectedArticle.quickAnswer || selectedArticle.metaDescription || selectedArticle.excerpt,
              "image": "https://power.ng/og-image.png",
              "author": {
                "@type": "Organization",
                "name": selectedArticle.author
              },
              "publisher": {
                "@type": "Organization",
                "name": "Power.ng",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://power.ng/web-app-manifest-512x512.png"
                }
              },
              "datePublished": selectedArticle.date,
              "dateModified": selectedArticle.dateModified || selectedArticle.date,
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": selectedArticleUrl
              },
              "keywords": selectedArticle.keywords.join(", ")
            })}
          </script>
          {faqSchema && (
            <script type="application/ld+json">
              {JSON.stringify(faqSchema)}
            </script>
          )}
        </Helmet>
        {/* Navigation Bar */}
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link
              to="/articles"
              className="flex items-center gap-2 text-gray-600 hover:text-[#008751] transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Articles
            </Link>
            <div className="flex gap-2">
              <button
                onClick={handleShare}
                title="Share this article"
                className="p-2 text-gray-400 hover:text-[#008751] hover:bg-green-50 rounded-full transition-colors"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Share Toast Notification */}
        {showShareToast && (
          <div className="fixed top-20 right-4 bg-[#008751] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-fade-in">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">Link copied to clipboard!</span>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
              onClick={() => setShowShareModal(false)}
            ></div>

            {/* Modal */}
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl z-50 w-full max-w-md mx-4 animate-fade-in">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Share this article</h3>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-3">
                  {/* WhatsApp - Most Popular */}
                  <button
                    onClick={shareToWhatsApp}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#25D366] hover:bg-[#1fbd5a] text-white transition-all transform hover:scale-105 shadow-md"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    <span className="font-semibold">Share on WhatsApp</span>
                  </button>

                  {/* Facebook */}
                  <button
                    onClick={shareToFacebook}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#1877F2] hover:bg-[#0d65d9] text-white transition-all transform hover:scale-105 shadow-md"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span className="font-semibold">Share on Facebook</span>
                  </button>

                  {/* Twitter/X */}
                  <button
                    onClick={shareToTwitter}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-black hover:bg-gray-800 text-white transition-all transform hover:scale-105 shadow-md"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    <span className="font-semibold">Share on X (Twitter)</span>
                  </button>

                  {/* LinkedIn */}
                  <button
                    onClick={shareToLinkedIn}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#0A66C2] hover:bg-[#084d93] text-white transition-all transform hover:scale-105 shadow-md"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    <span className="font-semibold">Share on LinkedIn</span>
                  </button>

                  {/* Telegram */}
                  <button
                    onClick={shareToTelegram}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#26A5E4] hover:bg-[#1f8ec4] text-white transition-all transform hover:scale-105 shadow-md"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                    <span className="font-semibold">Share on Telegram</span>
                  </button>

                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or</span>
                    </div>
                  </div>

                  {/* Copy Link */}
                  <button
                    onClick={copyToClipboard}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 transition-all transform hover:scale-105"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold">Copy Link</span>
                  </button>

                  {/* Native Share (Mobile) */}
                  {'share' in navigator && (
                    <button
                      onClick={useNativeShare}
                      className="w-full flex items-center gap-4 p-4 rounded-xl bg-[#008751] hover:bg-[#006d41] text-white transition-all transform hover:scale-105"
                    >
                      <Share2 className="w-6 h-6" />
                      <span className="font-semibold">More Options</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className={`flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full ${selectedArticle.category === 'Power Generation'
                ? 'bg-[#E8F5E9] text-[#006b40]'
                : selectedArticle.category === 'Renewable Energy'
                  ? 'bg-yellow-50 text-[#8a5a00]'
                  : 'bg-blue-50 text-blue-700'
                }`}>
                {getCategoryIcon(selectedArticle.category)}
                {selectedArticle.category}
              </span>
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                {selectedArticle.dateModified || selectedArticle.date}
              </span>
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                {selectedArticle.readTime}
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {selectedArticle.title}
            </h1>

            <div className="flex items-center gap-3 pb-6 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#008751]">
                <User className="w-6 h-6" />
              </div>
              <div>
                <div className="font-medium text-gray-900">{selectedArticle.author}</div>
                <div className="text-sm text-gray-500">Power.ng Editorial Team</div>
              </div>
            </div>

            <BrandAnswerBox
              quickAnswer={selectedArticle.quickAnswer || selectedArticle.metaDescription || selectedArticle.excerpt}
              path={selectedArticlePath}
            />
          </div>

          {/* In-Article Advertisement */}
          <GoogleAd
            className="mb-8"
            layout="in-article"
            slotId={AD_SLOTS.CONTENT_PAGE}
            devMode={false}
          />

          {/* Main Content */}
          <div id="article-main-content" className="bg-white rounded-2xl shadow-sm p-6 md:p-12 overflow-hidden">
            <div className="prose prose-lg max-w-none prose-green prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ node, ...props }) => (
                    <div className="my-6 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200 text-sm text-left" {...props} />
                    </div>
                  ),
                  thead: ({ node, ...props }) => (
                    <thead className="bg-gray-50 text-gray-900 font-bold" {...props} />
                  ),
                  th: ({ node, ...props }) => (
                    <th className="px-4 py-3 font-semibold text-gray-900" {...props} />
                  ),
                  td: ({ node, ...props }) => (
                    <td className="px-4 py-3 border-t border-gray-100 text-gray-700" {...props} />
                  ),
                  pre: ({ node, ...props }) => (
                    <div className="my-6 overflow-x-auto rounded-xl bg-gray-900 p-4 text-gray-100 text-sm font-mono leading-relaxed">
                      <pre className="whitespace-pre-wrap break-words" {...props} />
                    </div>
                  ),
                  code: ({ node, className, children, ...props }: any) => {
                    const isInline = !className && typeof children === 'string' && !children.includes('\n');
                    return isInline ? (
                      <code className="bg-gray-100 text-[#006b40] px-1.5 py-0.5 rounded text-sm font-mono font-medium" {...props}>
                        {children}
                      </code>
                    ) : (
                      <code className="bg-transparent p-0 text-inherit font-mono text-sm" {...props}>
                        {children}
                      </code>
                    );
                  },
                  blockquote: ({ node, children, ...props }) => {
                    return (
                      <blockquote className="my-6 border-l-4 border-[#008751] bg-green-50/60 p-4 rounded-r-xl text-gray-800 font-medium not-italic" {...props}>
                        {children}
                      </blockquote>
                    );
                  },
                }}
              >
                {selectedArticle.content}
              </ReactMarkdown>
            </div>

            {selectedArticle.faqs && selectedArticle.faqs.length > 0 && (
              <section className="mt-10 border-t border-gray-100 pt-8" aria-labelledby="article-faqs">
                <h2 id="article-faqs" className="text-2xl font-bold text-gray-900 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {selectedArticle.faqs.map((faq, index) => (
                    <div key={index} className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                      <h3 className="text-base font-semibold text-gray-900 mb-2">{faq.question}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <SolarLeadCTA source={selectedArticlePath} />

          {/* Related Articles Hint */}
          {selectedArticle.relatedArticles && (
            <div className="mt-8 border-t border-gray-200 pt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Related Topics</h3>
              <div className="flex flex-wrap gap-2">
                {selectedArticle.keywords.map((keyword, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    );
  }

  // Articles List View
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Energy Insights & Analysis - Power.ng</title>
        <meta name="description" content="Latest articles, guides, and analysis on Nigeria's power grid, renewable energy, and oil sector." />
        <link rel="canonical" href="https://power.ng/articles" />
        <meta property="og:title" content="Energy Insights & Analysis - Power.ng" />
        <meta property="og:description" content="Latest articles, guides, and analysis on Nigeria's power grid, renewable energy, and oil sector." />
        <meta property="og:url" content="https://power.ng/articles" />
      </Helmet>
      {/* Header */}
      <div className="bg-gradient-to-br from-[#C1272D] to-[#991f24] text-white py-12 relative overflow-hidden">
        {/* Abstract Pattern Overlay */}
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
            Energy Insights & Analysis
          </h1>
          <p className="text-xl text-white/90 max-w-3xl font-light">
            Analysis and updates on Nigeria's power sector, renewable energy trends, and oil market.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for grid updates, solar prices, tariffs..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008751] focus:border-transparent transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${selectedCategory === category
                    ? 'bg-[#008751] text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500 flex justify-between items-center">
            <span>Showing {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}</span>
            {searchTerm && <button onClick={() => setSearchTerm('')} className="text-[#008751] hover:underline">Clear search</button>}
          </div>
        </div>

        {/* Articles Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => {
              const ArticleCard = (
                <Link
                  key={article.id}
                  to={`/articles/${article.slug}`}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer border border-gray-100 flex flex-col h-full"
                >
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${article.category === 'Power Generation'
                        ? 'bg-[#E8F5E9] text-[#006b40]'
                        : article.category === 'Renewable Energy'
                          ? 'bg-yellow-50 text-[#8a5a00]'
                          : 'bg-blue-50 text-blue-700'
                        }`}>
                        {getCategoryIcon(article.category)}
                        {article.category}
                      </span>
                      {article.trending && (
                        <div className="flex items-center gap-1 text-[#C1272D] text-xs font-bold animate-pulse">
                          <TrendingUp className="w-3 h-3" />
                          <span>TRENDING</span>
                        </div>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#008751] transition-colors leading-tight line-clamp-2">
                      {article.title}
                    </h3>

                    <p className="text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed flex-1">
                      {article.excerpt}
                    </p>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          <span>{article.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{article.readTime}</span>
                        </div>
                      </div>

                      <div className="w-full bg-gray-50 text-[#008751] py-2.5 rounded-lg font-bold text-sm border border-gray-200 group-hover:bg-[#008751] group-hover:text-white group-hover:border-[#008751] transition-all flex items-center justify-center gap-2">
                        Read Article
                        <ArrowLeft className="w-4 h-4 rotate-180" />
                      </div>
                    </div>
                  </div>
                </Link>
              );

              // Inject Ad after the 6th article (index 5)
              if (index === 5) {
                return (
                  <Fragment key={`article-ad-${article.id}`}>
                    {ArticleCard}
                    <GoogleAd key="ad-list-1" slotId={AD_SLOTS.LIST_VIEW} devMode={false} />
                  </Fragment>
                );
              }

              return ArticleCard;
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-16 text-center border border-gray-100">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-600 max-w-md mx-auto">We couldn't find any articles matching "{searchTerm}". Try adjusting your search term or selecting a different category.</p>
            <button
              onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
              className="mt-6 text-[#008751] font-medium hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Related Tools CTA */}
        <div className="mt-20 bg-gradient-to-br from-[#008751] to-[#006d41] rounded-2xl p-10 text-center text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>

          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-4">
              Turn Insights Into Action
            </h2>
            <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
              Don't just read about energy—manage it. Use our interactive tools to calculate costs, size solar systems, and track grid status.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/tools"
                className="bg-white text-[#008751] px-8 py-3 rounded-xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all transform hover:-translate-y-1 block"
              >
                Launch Tools Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
